<template lang="pug">
.container(id="ScheduleDayTimeEditor-container")
  .container-restrictions
    .row.section-title
      p {{ $t('Day times') }}
    .row(v-if="entity[`${model}_DaytimeRestrictions`].length > 0")
      .column.column-left
      .column.column-middle
        .restriction-day(v-for="n in 7", :key="n")
          .day-label {{ $t("short" + days[n - 1]) }}
      .column.column-right
    .row.restriction(v-for="restriction in sortedRestrictions", :key="restriction.id")
      .column.column-left
        span.warning.validity(v-if='hasValidity(restriction.id)', v-html='showValidityFor(restriction.id)')
      .column.column-middle
        .restriction-day(v-for="n in 7", :key="n")
          .span(:class='{ allsame: allDaysSame(restriction), off: showDTRestrictionFor(days[n - 1], restriction.id) === "off", allday: showDTRestrictionFor(days[n - 1], restriction.id) === "24h" }') {{ showDTRestrictionFor(days[n - 1], restriction.id) }}
      .column.column-buttons
        el-button.button(
          :id="'ScheduleDayTimeEditor-deleteRestriction' + restriction.id + 'Button'",
          type="success",
          size="mini",
          @click="deleteTimeRestriction(restriction.id)",
        )
          span
            font-awesome-icon.icon(v-if="isDeleting !== restriction.id", icon='minus')
            font-awesome-icon.icon.fa-spin(v-else, icon='spinner')
            span &nbsp; {{ $t(isDeleting !== restriction.id ? 'delete' : 'deleting') }}
        el-button.button(
          :id="'ScheduleDayTimeEditor-editRestriction' + restriction.id + 'Button'",
          :title="hasValidity(restriction.id) ? `${restriction.validFrom} - ${restriction.validTo}` : $t('edit time restriction')",
          type="success",
          size="mini",
          :class='{ "warning": hasValidity(restriction.id) }',
          @click="editTimeRestriction(restriction)",
        )
          font-awesome-icon.icon(:icon="hasValidity(restriction.id) ? 'calendar-alt' : 'edit'")
          span &nbsp; {{ $t('edit') }}
  .container-form
    .row
      .column.column-left
      .column.column-middle
        el-switch.switch-mode(id="ScheduleDayTimeEditor-dayWeekSwitch" v-model='checkedAllWeek', :active-text="$t('all week')", :inactive-text="$t('days')")
      .column.column-right
    .row(v-if="!checkedAllWeek")
      .column.column-left
      .column.column-middle
        .restriction-day(v-for="n in 7", :key="n")
          .day-label {{ $t("short" + days[n - 1]) }}
      .column.column-right
    .row(v-if="!checkedAllWeek")
      .column.column-left
      .column.column-middle
        .restriction-day(v-for="n in 7", :key="n")
          TimeRangeSelector.time-range-selector(
            :id="'ScheduleDayTimeEditor-timeRangeSelectorDay' + n"
            :time-from="newTimeRestriction[n - 1].start",
            :time-to="newTimeRestriction[n - 1].end",
            :ref='"timeRangeSelector" + n',
            @adjustTime="(timeTo) => { adjustTime(timeTo, n) }",
            @change="(timeFrom, timeTo) => { timeRangeChange(timeFrom, timeTo, n) }",
            :flex_column='true'
          )
          el-button(type='text', size='mini', @click='setZeros(n)')
            font-awesome-icon(icon="ban", :class="{ danger: newTimeRestriction[n - 1].start === newTimeRestriction[n - 1].end }")
      .column.column-buttons
        el-button.button(
          id="ScheduleDayTimeEditor-addRestrictionDaysButton",
          type="success",
          size="mini"
          @click="addNewRestriction()",
        )
          span
            font-awesome-icon.icon(v-if="!isAdding", icon='plus')
            font-awesome-icon.icon.fa-spin(v-else, icon='spinner')
            span &nbsp; {{ $t(!isAdding ? 'add' : 'adding') }}
    .row(v-if="checkedAllWeek")
      .column.column-left
      .column.column-middle
        .restriction-day.checkedAllWeek
          .day-label {{ $t("short" + days[0]) }} - {{ $t("short" + days[6]) }}
      .column.column-right
    .row(v-if="checkedAllWeek")
      .column.column-left
      .column.column-middle
        .restriction-day.checkedAllWeek
          TimeRangeSelector.time-range-selector(
            id="ScheduleDayTimeEditor-timeRangeSelectorAllWeek"
            :time-from="newAllSameTimeRestriction.start",
            :time-to="newAllSameTimeRestriction.end",
            @change="weekTimeRangeChange",
            :flex_column='true'
          )
      .column.column-buttons
        el-button.button(
          id="ScheduleDayTimeEditor-addRestrictionAllWeekButton",
          type="success",
          size="mini"
          @click="addNewRestriction()",
        )
          span
            font-awesome-icon.icon(v-if="!isAdding", icon='plus')
            font-awesome-icon.icon.fa-spin(v-else, icon='spinner')
            span &nbsp; {{ $t(!isAdding ? 'add' : 'adding') }}
  el-dialog(v-if='editedTimeRestriction && editTimeRestrictionVisible', :close-on-click-modal="false", title='', :visible.sync='editTimeRestrictionVisible', append-to-body, width='50%')
    h4.header
      span {{ $t('Editing day-time restriction - ') }} {{ entity.name ? entity.name : entity.Medium.name }}
      .float-right
        font-awesome-icon.icon.danger(v-if="dateRangeDanger", icon='exclamation-circle')
        el-date-picker(
          :picker-options="{ firstDayOfWeek: 1 }",
          @change='calculateDaysActive()',
          v-model="editedTimeRestriction.validFrom",
          type="date",
          format='yyyy-MM-dd',
          size='mini',
          :title='$t("Leave blank for infinite")',
          :placeholder="$t('Valid from date')",
          :id="'ScheduleDayTimeEditor-datePickerValidFromRestriction' + editedTimeRestriction.id"
        )
        el-date-picker(
          :picker-options="{ firstDayOfWeek: 1 }",
          @change='calculateDaysActive()',
          v-model="editedTimeRestriction.validTo",
          type="date",
          format='yyyy-MM-dd',
          size='mini',
          :title='$t("Leave blank for infinite")',
          :placeholder="$t('Valid to date')",
          :id="'ScheduleDayTimeEditor-datePickerValidToRestriction' + editedTimeRestriction.id"
        )
    hr.header
    .container-form.editor
      .row.days
        .column.column-left
        .column.column-middle
          .restriction-day(v-for="n in 7", :key="n")
            .day-label(
              :class="{ warning: highlightedDays.includes(n) }"
            ) {{ $t("short" + days[n - 1]) }}
        .column.column-right
      .row
        .column.column-left
        .column.column-middle
          .restriction-day(v-for="n in 7", :key="n")
            TimeRangeSelector(
              :time-from="editedTimeRestriction[`start_${days[n - 1]}`]",
              :time-to="editedTimeRestriction[`end_${days[n - 1]}`]",
              :ref='"timeRangeSelector" + n',
              @adjustTime="(timeTo) => { adjustTime(timeTo, n) }",
              @change="(timeFrom, timeTo) => { timeRangeChangeEdited(timeFrom, timeTo, days[n - 1]) }",
              :flex_column='true'
            )
            el-button(type='text', size='mini', @click='setZeros(n)')
              font-awesome-icon(
                icon="ban",
                :class="{ danger: editedTimeRestriction[`start_${days[n - 1]}`] === editedTimeRestriction[`end_${days[n - 1]}`] }"
              )
      el-button.button(type="success", size='mini', @click="saveEditedRestriction()", :disabled='isSaving')
        font-awesome-icon.icon(icon="save")
        span &nbsp; {{ !isSaving ? $t('save') : $t('saving...')}} &nbsp;
</template>

<script>
import Vue from 'vue'
import TimeRangeSelector from '../Common/TimeRangeSelector'

export default {
  components: {
    TimeRangeSelector
  },
  props: ['item', 'model'],
  watch: {
    checkedAllWeek: async function (newVal, oldVal) {
      if (!newVal && oldVal) {
        this.newTimeRestriction.map(restr => {
          restr.start = this.newAllSameTimeRestriction.start
          restr.end = this.newAllSameTimeRestriction.end
        })
      }
    }
  },
  data () {
    return {
      errors: {},
      newTimeRestriction: this.defaultNewTimeRestriction(),
      newAllSameTimeRestriction: { start: '00:00', end: '24:00' },
      days: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday'
      ],
      checkedAllWeek: true,
      showAllWeekSummarizedRestrictions: false,
      isSaving: false,
      isAdding: false,
      isDeleting: null,
      editTimeRestrictionVisible: false,
      editedTimeRestriction: undefined,
      entity: JSON.parse(JSON.stringify(this.item)),
      highlightedDays: [],
      dateRangeDanger: false,
      pickerOptions: { firstDayOfWeek: 1 }
    }
  },
  methods: {
    setZeros (n) {
      this.$refs['timeRangeSelector' + n][0].setZeros()
    },
    adjustTime (timeTo, n) {
      if (n === 7) n = 0
      if (!this.editedTimeRestriction) {
        this.newTimeRestriction[n].start = '00:00'
        this.newTimeRestriction[n].end = timeTo
      } else {
        this.editedTimeRestriction[`start_${this.days[n]}`] = '00:00'
        this.editedTimeRestriction[`end_${this.days[n]}`] = timeTo
      }
    },
    editTimeRestriction (restriction) {
      const restr = this.entity[`${this.model}_DaytimeRestrictions`].find(
        restrctn => restrctn.id === restriction.id
      )
      this.editedTimeRestriction = JSON.parse(JSON.stringify(restr))
      this.editTimeRestrictionVisible = true
    },
    hasValidity (restrictionId) {
      const restriction = this.entity[`${this.model}_DaytimeRestrictions`].find(
        restrctn => restrctn.id === restrictionId
      )
      return restriction.validFrom.length > 0 || restriction.validTo.length > 0
    },
    showValidityFor (restrictionId) {
      const restriction = this.entity[`${this.model}_DaytimeRestrictions`].find(
        restrctn => restrctn.id === restrictionId
      )
      return `${restriction.validFrom ? restriction.validFrom.replace('<', '&lt;').replace('>', '&gt;') : '&infin;'}  &mdash; ${restriction.validTo ? restriction.validTo.replace('<', '&lt;').replace('>', '&gt;') : '&infin;'}`
    },
    calculateDaysActive () {
      this.highlightedDays = []
      this.dateRangeDanger = false
      const start = this.$moment(this.editedTimeRestriction.validFrom)
      const end = this.$moment(this.editedTimeRestriction.validTo)
      const diff = end.diff(start, 'days')
      if (diff < 0) {
        this.dateRangeDanger = true
      }
      const startDay = start.day() === 0 ? 7 : start.day()
      const endDay = end.day() === 0 ? 7 : end.day()
      if (diff >= 7) {
        this.highlightedDays = [ 1, 2, 3, 4, 5, 6, 7 ]
        return
      }
      if (startDay <= endDay) {
        for (let index = startDay; index <= endDay; index++) {
          this.highlightedDays.push(index)
        }
      } else {
        for (let index = startDay; index <= 7; index++) {
          this.highlightedDays.push(index)
        }
        for (let index = 1; index <= endDay; index++) {
          this.highlightedDays.push(index)
        }
      }
    },
    showDTRestrictionFor (day, restrictionId) {
      const restriction = this.entity[`${this.model}_DaytimeRestrictions`].find(
        restrctn => restrctn.id === restrictionId
      )

      if (restriction.validFrom && restriction.validTo) {
        const start = this.$moment(restriction.validFrom)
        const end = this.$moment(restriction.validTo)
        const diff = end.diff(start, 'days')
        if (diff < 0) {
          return '-'
        }
        const startDay = start.day() === 0 ? 7 : start.day()
        const endDay = end.day() === 0 ? 7 : end.day()
        let dayRange = []
        if (diff >= 7) {
          dayRange = [ 1, 2, 3, 4, 5, 6, 7 ]
        }
        if (startDay <= endDay) {
          for (let index = startDay; index <= endDay; index++) {
            dayRange.push(index)
          }
        } else {
          for (let index = startDay; index <= 7; index++) {
            dayRange.push(index)
          }
          for (let index = 1; index <= endDay; index++) {
            dayRange.push(index)
          }
        }
        if (!(dayRange.includes(this.days.indexOf(day) + 1))) {
          return ''
        }
      }

      if (restriction['start_' + day] === '00:00' && restriction['end_' + day] === '24:00') return '24h'
      if (restriction['start_' + day] === '00:00' && restriction['end_' + day] === '00:00') return 'off'
      if (restriction['start_' + day] === restriction['end_' + day]) return 'off'
      return restriction['start_' + day] + ' ' + restriction['end_' + day]
    },
    allDaysSame (restriction) {
      const startKeys = Object.keys(restriction).filter(k => k.startsWith('start_'))
      const valuesStartSet = new Set()
      for (const key of startKeys) {
        valuesStartSet.add(restriction[key])
      }
      const endKeys = Object.keys(restriction).filter(k => k.startsWith('end_'))
      const valuesEndSet = new Set()
      for (const key of endKeys) {
        valuesEndSet.add(restriction[key])
      }
      return (valuesStartSet.size === 1 && valuesEndSet.size === 1) &&
        // All are same but may be 'off' or '24h' though. Make sure it is not such a case, and check only Monday.
        !(restriction['start_Monday'] === '00:00' && restriction['end_Monday'] === '00:00') &&
        !(restriction['start_Monday'] === '00:00' && restriction['end_Monday'] === '24:00')
    },
    async deleteTimeRestriction (restrictionId) {
      if (this.isDeleting !== null) return
      this.isDeleting = restrictionId
      const path = `${this.model}_DaytimeRestriction/` + restrictionId
      await Vue.axios
        .delete(path)
        .then(response => {
          const restrictionIndex = this.entity[`${this.model}_DaytimeRestrictions`].findIndex(restriction => restriction.id === restrictionId)
          this.entity[`${this.model}_DaytimeRestrictions`].splice(restrictionIndex, 1)
        })
      this.isDeleting = null
    },
    defaultNewTimeRestriction () {
      return [
        { start: '00:00', end: '24:00' },
        { start: '00:00', end: '24:00' },
        { start: '00:00', end: '24:00' },
        { start: '00:00', end: '24:00' },
        { start: '00:00', end: '24:00' },
        { start: '00:00', end: '24:00' },
        { start: '00:00', end: '24:00' }
      ]
    },
    formatNewTimeRestrictionForAPI () {
      let formattedRestriction = {}
      if (!this.checkedAllWeek) {
        for (let index = 0; index < this.newTimeRestriction.length; index++) {
          const element = this.newTimeRestriction[index]
          formattedRestriction['start_' + this.days[index]] = element.start
          formattedRestriction['end_' + this.days[index]] = element.end
        }
      } else {
        for (let index = 0; index < this.days.length; index++) {
          formattedRestriction['start_' + this.days[index]] = this.newAllSameTimeRestriction.start
          formattedRestriction['end_' + this.days[index]] = this.newAllSameTimeRestriction.end
        }
      }
      formattedRestriction[this.model] = this.entity.id
      // always valid for new restriction
      formattedRestriction.validFrom = ''
      formattedRestriction.validTo = ''
      return formattedRestriction
    },
    async addNewRestriction () {
      if (this.isAdding) return
      this.isAdding = true
      const formattedRestriction = this.formatNewTimeRestrictionForAPI()
      const path = `${this.model}_DaytimeRestriction`
      await Vue.axios
        .post(path, formattedRestriction)
        .then(response => {
          this.newTimeRestriction = this.defaultNewTimeRestriction()
          formattedRestriction.id = response.data.id
          this.entity[`${this.model}_DaytimeRestrictions`].push(formattedRestriction)
        })
      this.$emit('reloadPlayListItem')
      this.isAdding = false
    },
    async saveEditedRestriction () {
      this.isSaving = true
      delete this.editedTimeRestriction.updatedAt
      this.editedTimeRestriction.validFrom = this.editedTimeRestriction.validFrom ? this.$moment(this.editedTimeRestriction.validFrom).format('YYYY-MM-DD') : ''
      this.editedTimeRestriction.validTo = this.editedTimeRestriction.validTo ? this.$moment(this.editedTimeRestriction.validTo).format('YYYY-MM-DD') : ''
      const path = `${this.model}_DaytimeRestriction/${this.editedTimeRestriction.id}`
      await Vue.axios.patch(path, this.editedTimeRestriction)
      this.entity[`${this.model}_DaytimeRestrictions`][this.entity[`${this.model}_DaytimeRestrictions`].findIndex(dtr => dtr.id === this.editedTimeRestriction.id)] = this.editedTimeRestriction
      this.isSaving = false
      this.editedTimeRestriction = undefined
      this.editTimeRestrictionVisible = false
      this.$emit('reloadPlayListItem')
    },
    timeRangeChange (timeFrom, timeTo, index) {
      this.newTimeRestriction[index - 1].start = timeFrom
      this.newTimeRestriction[index - 1].end = timeTo
    },
    timeRangeChangeEdited (timeFrom, timeTo, day) {
      this.editedTimeRestriction[`start_${day}`] = timeFrom
      this.editedTimeRestriction[`end_${day}`] = timeTo
    },
    weekTimeRangeChange (timeFrom, timeTo) {
      this.newAllSameTimeRestriction.start = timeFrom
      this.newAllSameTimeRestriction.end = timeTo
    }
  },
  computed: {
    sortedRestrictions () {
      const restrictions = this.entity[`${this.model}_DaytimeRestrictions`].slice()
      const validApplied = restrictions.find(r => r.validFrom !== '' || r.validTo !== '')
      const keyForSorting = validApplied ? 'validFrom' : 'start_Monday'
      console.log(keyForSorting)
      return restrictions.sort((a, b) => (a[keyForSorting] > b[keyForSorting]) ? 1 : ((b[keyForSorting] > a[keyForSorting]) ? -1 : 0))
    }
  }
}
</script>
<style lang="scss" scoped>
@import "../../../theme-m4b/src/common/var.scss";
.container-restrictions, .container-form, .container-edit {
  display: flex;
  flex-direction: column;
  font-size: $--font-size-base;
  margin: auto;
  width: 95%;
  .row {
    display: flex;
    margin-bottom: 10px;
    &.section-title {
      font-size: $--font-size-big;
      font-weight: bold;
    }
    .column {
      &.column-left {
        width: 260px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        .validity {
          margin-left: auto;
        }
      }
      &.column-middle {
        display: flex;
        width: 460px;
        margin-left: 5px;
        .restriction-day {
          word-spacing: 100vw;
          width: 166px;
          text-align: center;
          display: flex;
          flex-direction: column;
          justify-content: center;
          .day-label {
            color: $--color-text-secondary;
            &.warning {
              color: #e6a23c;
            }
          }
          .off {
            color: $--color-danger;
          }
          .allday {
            color: $--color-success;
          }
          .allsame {
            color: $--color-text-secondary;
          }
        }
      }
      &.column-buttons {
        display: flex;
        align-items: center;
        .button {
          max-height: 30px;
          width: 95px;
        }
      }
    }
  }
}
.container-form, .container-edit {
  margin-top: 30px;
  border-bottom: 1px $--border-style-base rgba($--border-color-extra-light, 0.2);
  .restriction-day {
    &.checkedAllWeek {
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 177px;
      .time-range-selector {
        max-width: 62px;
      }
    }
  }
  &.editor {
    .column {
      &.column-left {
        width: 170px;
      }
      &.column-middle {
        display: flex;
        width: 460px;
        margin-left: 10px;
      }
    }
  }
  .button {
    display: block;
    margin-left: auto;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/Common/ScheduleDayTimeEditor.vue